import { fetchAdminSettings, patchAdminSettings, type IAdminSettings } from '@/api/admin';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { CardContent } from '@/components/ui/card';
import SettingsWidgetCard from './settings-widget-card';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { $updateFeatures } from '@/entities/features-entity';
import type { DeliveryProfile, SenderProxyConfig } from '@/entities/sender.entity';
import { createDefaultDeliveryProfile, createDefaultSenderProxyConfig } from '@/entities/sender.entity';
import SenderDeliveryProfileForm, {
	defaultAdminDeliveryProfile,
} from '@/pages/admin/settings/sender-delivery-profile-form';
import SenderProxyMiniSummary from '@/pages/admin/settings/sender-proxy-mini-summary';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import { useUnit } from 'effector-react';
import { SendIcon } from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

const MIN_CONCURRENT_RUNS = 1;
const MAX_CONCURRENT_RUNS = 100;
const MIN_UPLOAD_MB = 1;
const MAX_UPLOAD_MB = 256;
const MIN_PIPELINE_INFLIGHT = 1;
const MAX_PIPELINE_INFLIGHT = 100;

export default function SenderSettingsWidget() {
	const updateFeatures = useUnit($updateFeatures);
	const [senderEnabled, setSenderEnabled] = useState(false);
	const [senderWebappEnabled, setSenderWebappEnabled] = useState(false);
	const [senderPresetsEnabled, setSenderPresetsEnabled] = useState(true);
	const [senderAutoCampaignEnabled, setSenderAutoCampaignEnabled] = useState(true);
	const [senderConsoleEnabled, setSenderConsoleEnabled] = useState(true);
	const [senderHubProfileSettingsVisible, setSenderHubProfileSettingsVisible] = useState(true);
	const [senderHubAuthJobsVisible, setSenderHubAuthJobsVisible] = useState(true);
	const [senderLandingAuthEnabled, setSenderLandingAuthEnabled] = useState(false);
	const [senderAutoRunEnabled, setSenderAutoRunEnabled] = useState(false);
	const [senderDeviceAutoEnabled, setSenderDeviceAutoEnabled] = useState(false);
	const [pipelineMaxInflightInput, setPipelineMaxInflightInput] = useState('10');
	const [maxConcurrentInput, setMaxConcurrentInput] = useState('1');
	const [uploadMaxMbInput, setUploadMaxMbInput] = useState('10');
	const [deliveryProfile, setDeliveryProfile] = useState<DeliveryProfile>(defaultAdminDeliveryProfile());
	const [senderProxyConfig, setSenderProxyConfig] = useState<SenderProxyConfig>(createDefaultSenderProxyConfig());
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [loadError, setLoadError] = useState(false);

	function applySettings(settings: IAdminSettings) {
		setSenderEnabled(settings.senderEnabled ?? false);
		setSenderWebappEnabled(settings.senderWebappEnabled ?? false);
		setSenderPresetsEnabled(settings.senderPresetsEnabled ?? true);
		setSenderAutoCampaignEnabled(settings.senderAutoCampaignEnabled ?? true);
		setSenderConsoleEnabled(settings.senderConsoleEnabled ?? true);
		setSenderHubProfileSettingsVisible(settings.senderHubProfileSettingsVisible ?? true);
		setSenderHubAuthJobsVisible(settings.senderHubAuthJobsVisible ?? true);
		setSenderLandingAuthEnabled(settings.senderLandingAuthEnabled ?? false);
		setSenderAutoRunEnabled(settings.senderAutoRunEnabled ?? false);
		setSenderDeviceAutoEnabled(settings.senderDeviceAutoEnabled ?? false);
		setPipelineMaxInflightInput(String(settings.senderDevicePipelineMaxInflight ?? 10));
		setMaxConcurrentInput(String(settings.senderMaxConcurrentRuns ?? 1));
		setUploadMaxMbInput(String(settings.senderSessionUploadMaxMb ?? 10));
		setDeliveryProfile(settings.senderDeliveryProfile ?? createDefaultDeliveryProfile());
		setSenderProxyConfig(settings.senderProxyConfig ?? createDefaultSenderProxyConfig());
		setLoadError(false);
	}

	const handleProxyCountChange = useCallback(
		(count: number) => setSenderProxyConfig(current => ({ ...current, proxyCount: count })),
		[],
	);

	const reload = useCallback(async () => {
		setIsLoading(true);
		try {
			const settings = await fetchAdminSettings();
			applySettings(settings);
		} catch {
			setLoadError(true);
			toast.error('Не удалось загрузить настройки sender');
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		void reload();
	}, [reload]);

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();

		if (loadError) {
			toast.error('Настройки не загружены — сохранение недоступно');
			return;
		}

		const senderMaxConcurrentRuns = Number(maxConcurrentInput);
		const senderSessionUploadMaxMb = Number(uploadMaxMbInput);
		const senderDevicePipelineMaxInflight = Number(pipelineMaxInflightInput);

		if (
			senderProxyConfig.enabled &&
			senderProxyConfig.proxyCount === 0
		) {
			toast.error('Загрузите прокси перед включением пула');
			return;
		}

		if (
			!senderProxyConfig.checkDomain.trim() ||
			senderProxyConfig.maxAttempts < 1 ||
			senderProxyConfig.maxAttempts > 20 ||
			senderProxyConfig.checkTimeoutSec < 1 ||
			senderProxyConfig.checkTimeoutSec > 30
		) {
			toast.error('Проверьте настройки прокси');
			return;
		}

		if (
			!maxConcurrentInput.trim() ||
			!Number.isInteger(senderMaxConcurrentRuns) ||
			senderMaxConcurrentRuns < MIN_CONCURRENT_RUNS ||
			senderMaxConcurrentRuns > MAX_CONCURRENT_RUNS
		) {
			toast.error(`Макс. параллельных run: от ${MIN_CONCURRENT_RUNS} до ${MAX_CONCURRENT_RUNS}`);
			return;
		}

		if (
			!uploadMaxMbInput.trim() ||
			!Number.isInteger(senderSessionUploadMaxMb) ||
			senderSessionUploadMaxMb < MIN_UPLOAD_MB ||
			senderSessionUploadMaxMb > MAX_UPLOAD_MB
		) {
			toast.error(`Лимит загрузки .session: от ${MIN_UPLOAD_MB} до ${MAX_UPLOAD_MB} МБ`);
			return;
		}

		if (
			!pipelineMaxInflightInput.trim() ||
			!Number.isInteger(senderDevicePipelineMaxInflight) ||
			senderDevicePipelineMaxInflight < MIN_PIPELINE_INFLIGHT ||
			senderDevicePipelineMaxInflight > MAX_PIPELINE_INFLIGHT
		) {
			toast.error(`Inflight device auth: от ${MIN_PIPELINE_INFLIGHT} до ${MAX_PIPELINE_INFLIGHT}`);
			return;
		}

		setIsSaving(true);
		try {
			const settings = await patchAdminSettings({
				senderEnabled,
				senderWebappEnabled,
				senderPresetsEnabled,
				senderAutoCampaignEnabled,
				senderConsoleEnabled,
				senderHubProfileSettingsVisible,
				senderHubAuthJobsVisible,
				senderLandingAuthEnabled,
				senderAutoRunEnabled,
				senderDeviceAutoEnabled,
				senderDevicePipelineMaxInflight,
				senderMaxConcurrentRuns,
				senderSessionUploadMaxMb,
				senderDeliveryProfile: {
					...deliveryProfile,
					delivery: { ...deliveryProfile.delivery, caption: null },
				},
				senderProxyConfig: {
					enabled: senderProxyConfig.enabled,
					protocol: senderProxyConfig.protocol,
					format: senderProxyConfig.format,
					maxAttempts: senderProxyConfig.maxAttempts,
					checkDomain: senderProxyConfig.checkDomain.trim(),
					checkTimeoutSec: senderProxyConfig.checkTimeoutSec,
					proxyCount: senderProxyConfig.proxyCount,
				},
			});
			applySettings(settings);
			updateFeatures({
				senderEnabled: settings.senderEnabled,
				senderWebappEnabled: settings.senderWebappEnabled,
				senderHubProfileSettingsVisible: settings.senderHubProfileSettingsVisible ?? true,
				senderHubAuthJobsVisible: settings.senderHubAuthJobsVisible ?? true,
			});
			toast.success('Настройки sender сохранены');
		} finally {
			setIsSaving(false);
		}
	}

	return (
		<SettingsWidgetCard
			id='sender'
			icon={SendIcon}
			title='Telegram Sender'
			description='Mass-send через Telethon и web_delete'
			titleClassName='text-chart-2'
			iconWrapperClassName='bg-chart-2/15'
		>
			<LoadingWrapper show={isSaving}>
				<CardContent className='px-4 py-4'>
					{loadError && !isLoading ? (
						<LandingsErrorState
							message='Настройки sender недоступны. Проверьте соединение с back.'
							onRetry={() => void reload()}
						/>
					) : (
					<form className='flex flex-col gap-4' onSubmit={handleSubmit}>
						<div className='flex items-center justify-between gap-3'>
							<div className='flex flex-col gap-0.5'>
								<Label htmlFor='sender-enabled'>Модуль sender</Label>
								<span className='text-xs text-muted-foreground'>Разрешить пользователям mass-send</span>
							</div>
							<Switch
								id='sender-enabled'
								checked={senderEnabled}
								onCheckedChange={(checked) => {
									setSenderEnabled(checked);
									if (!checked) setSenderWebappEnabled(false);
								}}
								disabled={isLoading}
							/>
						</div>

						<div className='flex items-center justify-between gap-3'>
							<div className='flex flex-col gap-0.5'>
								<Label htmlFor='sender-webapp-enabled'>Отдельный Sender WebApp</Label>
								<span className='text-xs text-muted-foreground'>
									Скрыть tab в основной панели; операторы заходят через sender-бота и /sender-app/
								</span>
							</div>
							<Switch
								id='sender-webapp-enabled'
								checked={senderWebappEnabled}
								onCheckedChange={setSenderWebappEnabled}
								disabled={isLoading || !senderEnabled}
							/>
						</div>

						<div className='flex items-center justify-between gap-3'>
							<div className='flex flex-col gap-0.5'>
								<Label htmlFor='sender-presets-enabled'>Пресеты рассылки (EVA)</Label>
								<span className='text-xs text-muted-foreground'>
									Именованные настройки доставки в Sender WebApp
								</span>
							</div>
							<Switch
								id='sender-presets-enabled'
								checked={senderPresetsEnabled}
								onCheckedChange={setSenderPresetsEnabled}
								disabled={isLoading || !senderEnabled}
							/>
						</div>

						<div className='flex items-center justify-between gap-3'>
							<div className='flex flex-col gap-0.5'>
								<Label htmlFor='sender-auto-campaign-enabled'>Автокампания (EVA)</Label>
								<span className='text-xs text-muted-foreground'>
									Очередь сессий и round-robin по пресетам (Wave 2+)
								</span>
							</div>
							<Switch
								id='sender-auto-campaign-enabled'
								checked={senderAutoCampaignEnabled}
								onCheckedChange={setSenderAutoCampaignEnabled}
								disabled={isLoading || !senderEnabled}
							/>
						</div>

						<div className='flex items-center justify-between gap-3'>
							<div className='flex flex-col gap-0.5'>
								<Label htmlFor='sender-console-enabled'>Консоль спамера (EVA)</Label>
								<span className='text-xs text-muted-foreground'>
									Ручной запуск run из Sender WebApp
								</span>
							</div>
							<Switch
								id='sender-console-enabled'
								checked={senderConsoleEnabled}
								onCheckedChange={setSenderConsoleEnabled}
								disabled={isLoading || !senderEnabled}
							/>
						</div>

						<div className='flex items-center justify-between gap-3'>
							<div className='flex flex-col gap-0.5'>
								<Label htmlFor='sender-hub-profile-settings'>Кнопка «Настройки» в Sender</Label>
								<span className='text-xs text-muted-foreground'>
									Build и caption для лендинга на главной Sender
								</span>
							</div>
							<Switch
								id='sender-hub-profile-settings'
								checked={senderHubProfileSettingsVisible}
								onCheckedChange={setSenderHubProfileSettingsVisible}
								disabled={isLoading}
							/>
						</div>

						<div className='flex items-center justify-between gap-3'>
							<div className='flex flex-col gap-0.5'>
								<Label htmlFor='sender-hub-auth-jobs'>Кнопка «Auth jobs» в Sender</Label>
								<span className='text-xs text-muted-foreground'>Landing / device auth на главной Sender</span>
							</div>
							<Switch
								id='sender-hub-auth-jobs'
								checked={senderHubAuthJobsVisible}
								onCheckedChange={setSenderHubAuthJobsVisible}
								disabled={isLoading}
							/>
						</div>

						<div className='flex items-center justify-between gap-3'>
							<div className='flex flex-col gap-0.5'>
								<Label htmlFor='sender-landing-auth'>Landing auth</Label>
								<span className='text-xs text-muted-foreground'>Telegram auth на лендингах</span>
							</div>
							<Switch
								id='sender-landing-auth'
								checked={senderLandingAuthEnabled}
								onCheckedChange={setSenderLandingAuthEnabled}
								disabled={isLoading}
							/>
						</div>

						<div className='flex items-center justify-between gap-3'>
							<div className='flex flex-col gap-0.5'>
								<Label htmlFor='sender-auto-run'>Auto-run после confirm</Label>
								<span className='text-xs text-muted-foreground'>Автозапуск рассылки с лендинга</span>
							</div>
							<Switch
								id='sender-auto-run'
								checked={senderAutoRunEnabled}
								onCheckedChange={setSenderAutoRunEnabled}
								disabled={isLoading}
							/>
						</div>

						<div className='flex items-center justify-between gap-3'>
							<div className='flex flex-col gap-0.5'>
								<Label htmlFor='sender-device-auto'>Device auto auth</Label>
								<span className='text-xs text-muted-foreground'>Авто-auth с устройства после launch (SMS/push)</span>
							</div>
							<Switch
								id='sender-device-auto'
								checked={senderDeviceAutoEnabled}
								onCheckedChange={setSenderDeviceAutoEnabled}
								disabled={isLoading}
							/>
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='sender-pipeline-max-inflight'>Inflight device auth</Label>
							<span className='text-xs text-muted-foreground'>
								Макс. jobs в waiting_for_code / confirming / session_ready (v2)
							</span>
							<InputGroup>
								<InputGroupInput
									id='sender-pipeline-max-inflight'
									type='number'
									inputMode='numeric'
									min={MIN_PIPELINE_INFLIGHT}
									max={MAX_PIPELINE_INFLIGHT}
									value={pipelineMaxInflightInput}
									onChange={e => setPipelineMaxInflightInput(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<Separator />

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='sender-max-concurrent'>Макс. параллельных run</Label>
							<span className='text-xs text-muted-foreground'>Очередь worker при превышении лимита</span>
							<InputGroup>
								<InputGroupInput
									id='sender-max-concurrent'
									type='number'
									inputMode='numeric'
									min={MIN_CONCURRENT_RUNS}
									max={MAX_CONCURRENT_RUNS}
									value={maxConcurrentInput}
									onChange={e => setMaxConcurrentInput(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='sender-upload-max-mb'>Лимит загрузки .session (МБ)</Label>
							<span className='text-xs text-muted-foreground'>Multipart guard на POST /runs</span>
							<InputGroup>
								<InputGroupInput
									id='sender-upload-max-mb'
									type='number'
									inputMode='numeric'
									min={MIN_UPLOAD_MB}
									max={MAX_UPLOAD_MB}
									value={uploadMaxMbInput}
									onChange={e => setUploadMaxMbInput(e.target.value)}
									disabled={isLoading}
								/>
							</InputGroup>
						</div>

						<Separator />

						<SenderProxyMiniSummary
							value={senderProxyConfig}
							onChange={setSenderProxyConfig}
							onProxyCountChange={handleProxyCountChange}
							disabled={isLoading || isSaving}
						/>

						<Separator />

						<SenderDeliveryProfileForm
							value={deliveryProfile}
							onChange={setDeliveryProfile}
							disabled={isLoading || isSaving}
						/>

						<Button type='submit' className='self-start' disabled={isLoading || isSaving}>
							Сохранить
						</Button>
					</form>
					)}
				</CardContent>
			</LoadingWrapper>
		</SettingsWidgetCard>
	);
}
